import { t } from "@lingui/macro";
import { MoonIcon, SunIcon } from "@phosphor-icons/react";
import { useTheme } from "@reactive-resume/hooks";
import { Button } from "@reactive-resume/ui";
import type { Variants } from "framer-motion";
import { motion } from "framer-motion";
import { useMemo } from "react";

type Props = {
  size?: number;
  className?: string;
};

/**
 * ThemeSwitch — binary light ↔ dark toggle.
 *
 * The underlying `useTheme()` hook supports a third "system" value that
 * follows OS preference, but the public UI only exposes Light / Dark to
 * keep the affordance obvious. If `theme === "system"`, we collapse it to
 * Dark (because the inline theme bootstrap script already resolved the
 * effective mode at first paint, and dark is the more likely click
 * target for a user who's now toggling).
 */
export const ThemeSwitch = ({ size = 20, className }: Props) => {
  const { theme, isDarkMode, setTheme } = useTheme();

  const variants: Variants = useMemo(
    () => ({
      light: { x: 0 },
      dark: { x: size * -1 },
    }),
    [size],
  );

  // Treat "system" as dark in the toggle's visual state — see comment above.
  const visualState: "light" | "dark" =
    theme === "light" ? "light" : (isDarkMode || theme === "dark" ? "dark" : "light");

  const handleToggle = () => {
    setTheme(visualState === "dark" ? "light" : "dark");
  };

  return (
    <Button
      size="icon"
      variant="ghost"
      className={className}
      aria-label={visualState === "dark" ? t`Switch to Light Mode` : t`Switch to Dark Mode`}
      onClick={handleToggle}
    >
      <div className="cursor-pointer overflow-hidden" style={{ width: size, height: size }}>
        <motion.div animate={visualState} variants={variants} className="flex">
          <SunIcon size={size} className="shrink-0" aria-hidden="true" />
          <MoonIcon size={size} className="shrink-0" aria-hidden="true" />
        </motion.div>
      </div>
    </Button>
  );
};
